import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { takeDraft, useDraftStore } from "../../../agent";
import { isRefusal, refusalText } from "../../../api/errors";
import { useApiCommand } from "../../../api/hooks";
import type { AddByHandDraft, ElementViewWithWarnings } from "../../../api/types";
import { addByHandPath } from "../../../app/paths";
import { Banner, Button, Card } from "../../../ui";
import { FormError } from "../components/fields";
import { Badges } from "../components/marks";
import { NameFields, NeededByFields, emptyCommon, neededByOf, textToSend, validateCommon, type CommonValues } from "../forms/Common";
import { KIND_SPECS } from "../forms";
import type { Errors, KindSpec, Values } from "../forms/types";
import { useCommandId, useRefresh } from "../hooks";
import { elementBadges } from "../lib/badges";
import { elementPath, symbolsPath } from "../lib/paths";
import { isAddableKind, kindWord, type AddableKind } from "../lib/words";

const GROUPS: Array<{ title: string; kinds: AddableKind[] }> = [
  { title: "Items and their structure", kinds: ["item", "family", "sub_family", "component_kind", "component", "capability"] },
  { title: "Data", kinds: ["data_field", "data_group", "pick_list"] },
  { title: "Documents and links", kinds: ["document_kind", "document_link", "link"] },
];

/**
 * Where the element sits in the family tree, when the address says so (a link from the family navigator, "Add a family
 * here"). It fills in the form, as a draft of the agent would, but is not shown as the agent's work.
 */
function seedFromAddress(params: URLSearchParams, kind: string | null): AddByHandDraft | null {
  const library = params.get("library");
  const family = params.get("family");
  const subFamily = params.get("sub_family");
  if (!isAddableKind(kind) || (!library && !family && !subFamily)) return null;
  if (kind !== "item" && kind !== "family" && kind !== "sub_family" && kind !== "component_kind") return null;
  return {
    kind: kind as AddByHandDraft["kind"],
    name: "",
    definition: "",
    drawn_on: [],
    is_connection: false,
    abstract: false,
    library_id: library,
    family_id: family,
    sub_family_id: subFamily,
  };
}

/** The address to go back to, when it is a page of the Deliverables view (no other address is followed). */
function deliverablesAddress(value: string | null): string | null {
  return value && /^\/deliverables(\?[^#]*)?$/.test(value) ? value : null;
}

/** The screen at /elements/new: pick the kind of element, then fill in its form. */
export function AddByHandPage() {
  const [params, setParams] = useSearchParams();
  // A draft the agent left in the store before the page opened. It is only read here; the store is cleared in an effect.
  const [draft, setDraft] = useState<AddByHandDraft | null>(() => useDraftStore.getState().draft);
  const [draftId, setDraftId] = useState(0);
  // Which screen filled the draft, when it was not the agent (read before the store is cleared).
  const [origin, setOrigin] = useState<string | null>(() => (useDraftStore.getState().draft ? useDraftStore.getState().origin : null));
  const sessionId = params.get("session");
  const requested = params.get("kind");
  const back = params.get("back") === "shared" ? ("shared" as const) : null;
  const toDeliverables = deliverablesAddress(params.get("return"));

  // The agent may fill the form for an item, a component kind or a document kind. The person reviews and sends it;
  // the agent never sends it (DEC-1016-90). A draft that arrives while the page is open replaces the form.
  useEffect(() => {
    if (useDraftStore.getState().draft) takeDraft();
    return useDraftStore.subscribe((state) => {
      if (!state.draft) return;
      const from = useDraftStore.getState().origin;
      const taken = takeDraft();
      if (!taken) return;
      setOrigin(from);
      setDraft(taken);
      setDraftId((n) => n + 1);
      setParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          next.set("kind", taken.kind);
          return next;
        },
        { replace: true },
      );
    });
  }, [setParams]);

  const kind = isAddableKind(requested) ? requested : draft ? draft.kind : null;
  const seed = seedFromAddress(params, kind);

  const pickAnother = () => {
    setDraft(null);
    setParams({ ...(sessionId ? { session: sessionId } : {}), ...(back ? { back } : {}), ...(toDeliverables ? { return: toDeliverables } : {}) }, { replace: false });
  };

  return (
    <div className="stack item-screen">
      <Card aria-labelledby="add-title">
        <h1 id="add-title">Add an element by hand</h1>
        {!kind && <p>Pick the kind of element to add. The app makes its code and checks that it is not used already.</p>}
        {kind && (
          <p>
            <Button variant="quiet" onClick={pickAnother}>
              Pick another kind
            </Button>
          </p>
        )}
        {back && (
          <p>
            <Link to="/shared">Back to Shared parts</Link>
          </p>
        )}
        {toDeliverables && (
          <p>
            <Link to={toDeliverables}>Back to Deliverables</Link>
          </p>
        )}
      </Card>
      {!kind && <KindChooser sessionId={sessionId} back={back} />}
      {kind && <AddForm key={`${kind}:${draftId}`} spec={KIND_SPECS[kind]} draft={draft?.kind === kind ? draft : null} seed={seed} sessionId={sessionId} back={back} fromView={draft?.kind === kind && origin === "deliverables"} />}
    </div>
  );
}

function KindChooser({ sessionId, back }: { sessionId: string | null; back: "shared" | null }) {
  const suffix = (sessionId ? `&session=${encodeURIComponent(sessionId)}` : "") + (back ? `&back=${back}` : "");
  return (
    <>
      {GROUPS.map((group) => (
        <Card key={group.title} aria-label={group.title}>
          <h2>{group.title}</h2>
          <ul className="kind-grid">
            {group.kinds.map((kind) => {
              const spec = KIND_SPECS[kind];
              return (
                <li key={kind}>
                  <Link className="kind-tile" to={`/elements/new?kind=${kind}${suffix}`}>
                    <strong>{spec.title}</strong>
                    <span className="muted">{spec.blurb}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </Card>
      ))}
    </>
  );
}

function AddForm({ spec, draft, seed, sessionId, back, fromView }: { spec: KindSpec; draft: AddByHandDraft | null; seed: AddByHandDraft | null; sessionId: string | null; back: "shared" | null; fromView: boolean }) {
  const start = draft ?? seed;
  const [common, setCommon] = useState<CommonValues>(() => emptyCommon(start));
  const [values, setValues] = useState<Values>(() => spec.empty(start));
  const [errors, setErrors] = useState<Errors>({});
  const [done, setDone] = useState<ElementViewWithWarnings | null>(null);
  const add = useApiCommand("add_by_hand");
  const ids = useCommandId();
  const refresh = useRefresh();
  const topRef = useRef<HTMLDivElement>(null);
  const Fields = spec.Fields;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const found = { ...validateCommon(common, spec, values), ...spec.validate(values, "add") };
    setErrors(found);
    if (Object.keys(found).length > 0) {
      topRef.current?.scrollIntoView?.({ block: "start" });
      return;
    }
    const text = textToSend(common, spec, values);
    add.mutate(
      {
        command_id: ids.current(),
        kind: spec.kind,
        element_name: text.name,
        definition: text.definition,
        content: spec.toContent(values),
        lines: spec.lines?.(values) ?? [],
        needed_by: neededByOf(common),
        drawn_on: spec.drawnOn?.(values) ?? [],
        code: common.code.trim() || null,
        session_id: sessionId,
      },
      {
        onSuccess: (reply) => {
          ids.renew();
          refresh("list_elements", "family_tree", "element", "item_page", "item_snapshot", "inbox", "find_in_ontology", "home");
          setDone(reply.data);
        },
      },
    );
  };

  if (done) {
    return <Added view={done} spec={spec} drawnOn={spec.drawnOn?.(values) ?? []} sessionId={sessionId} back={back} onAnother={() => {
      setDone(null);
      setValues(spec.empty(null));
      setCommon(emptyCommon(null));
      setErrors({});
    }} />;
  }

  const taken = isRefusal(add.error, "code_taken") ? add.error : null;
  const takenId = taken && typeof (taken.details as { element_id?: unknown } | null)?.element_id === "string" ? (taken.details as { element_id: string }).element_id : null;
  const problems = Object.keys(errors).length;

  return (
    <form onSubmit={submit} noValidate aria-labelledby="form-title" className="stack">
      <Card>
        <div ref={topRef} />
        <h2 id="form-title">{spec.title}</h2>
        <p className="muted">{spec.blurb}</p>
        {draft && fromView && (
          <Banner tone="demo">
            <p>Part of this form is filled in from the deliverable line. Look at every answer, change what is wrong, then press Add.</p>
          </Banner>
        )}
        {draft && !fromView && (
          <Banner tone="demo">
            <p className="agent-text">The agent filled in part of this form. Look at every answer, change what is wrong, then press Add. The agent never sends it.</p>
          </Banner>
        )}
        {problems > 0 && (
          <p role="alert" className="form-error">
            {problems === 1 ? "One answer needs your attention." : `${problems} answers need your attention.`} They are marked below.
          </p>
        )}
        <NameFields spec={spec} values={common} errors={errors} showCode onChange={(patch) => setCommon((c) => ({ ...c, ...patch }))} />
      </Card>
      <Card aria-label={`${spec.title} details`}>
        <Fields values={values} errors={errors} mode="add" set={(patch) => setValues((v) => ({ ...v, ...patch }))} />
      </Card>
      <Card aria-label="Needed by">
        <NeededByFields values={common} onChange={(patch) => setCommon((c) => ({ ...c, ...patch }))} />
      </Card>
      <Card>
        <div className="row">
          <Button type="submit" variant="primary" busy={add.isPending}>
            Add {kindWord(spec.kind).toLowerCase()}
          </Button>
          <Link to={back ? "/shared" : addByHandPath({ session: sessionId })}>Cancel</Link>
        </div>
        <FormError message={add.error ? refusalText(add.error) : null} />
        {takenId && (
          <p>
            <Link to={`/elements/${encodeURIComponent(takenId)}`}>Open the element that uses this code</Link>
          </p>
        )}
      </Card>
    </form>
  );
}

/** "a" or "an", by the first letter of the word that follows. */
function article(word: string): string {
  return /^[aeiou]/i.test(word.trim()) ? "an" : "a";
}

function Added({
  view,
  spec,
  drawnOn,
  sessionId,
  back,
  onAnother,
}: {
  view: ElementViewWithWarnings;
  spec: KindSpec;
  drawnOn: string[];
  /** The session the element was added for: the way back to it is offered (CP-06). */
  sessionId: string | null;
  back: "shared" | null;
  onAnother: () => void;
}) {
  const row = view.element;
  const errors = view.checks.filter((c) => c.severity === "error");
  return (
    <Card aria-labelledby="added-title">
      <h2 id="added-title">{row.name} is added</h2>
      <p>
        It is {article(kindWord(row.kind))} {kindWord(row.kind).toLowerCase()} with the code <code>{row.code}</code>. <Badges badges={elementBadges(row)} />
      </p>
      <p className="muted">The SME validates it in a hand-over. Until then it shows in the files as waiting for the SME.</p>
      {view.warnings && view.warnings.length > 0 && (
        <div className="notice notice--warn">
          <p>It looks like something that is already there:</p>
          <ul>
            {view.warnings.map((warning, index) => (
              <li key={index}>{warning}</li>
            ))}
          </ul>
        </div>
      )}
      {errors.length > 0 && (
        <div className="notice notice--warn">
          <p>{errors.length === 1 ? "One check needs your attention:" : `${errors.length} checks need your attention:`}</p>
          <ul>
            {errors.map((check, index) => (
              <li key={index}>{check.message}</li>
            ))}
          </ul>
        </div>
      )}
      <div className="row">
        <Link className="button button--primary" to={elementPath(row)}>
          Open it
        </Link>
        {drawnOn.length > 0 && (spec.kind === "item" || spec.kind === "component_kind") && (
          <Link className="button button--secondary" to={symbolsPath(row.id)}>
            Pick its symbol
          </Link>
        )}
        <Button onClick={onAnother}>Add another</Button>
        {back && (
          <Link className="button button--secondary" to="/shared">
            Back to Shared parts
          </Link>
        )}
        {sessionId && (
          <Link className="button button--secondary" to={`/sessions/${encodeURIComponent(sessionId)}`}>
            Back to the session
          </Link>
        )}
      </div>
    </Card>
  );
}
